// schema.config.js — Central JSON-LD structured data for EL Connect.
// Components import helpers from here instead of hardcoding schema objects.
//
// Exports:
//   ORGANIZATION  — schema.org/Organization (used on every page via index.html + LocalBusiness component)
//   getWebSiteSchema()      — schema.org/WebSite + SearchAction (homepage only)
//   getBreadcrumbs(route)   — schema.org/BreadcrumbList (all public pages)

const BASE_URL = 'https://elconnect.sg';

// ---------------------------------------------------------------------------
// S1 — Organization
// Full data in one place. index.html static block + CommonLocalBusinessSchemaComponent
// both read from this shape (index.html is updated separately via the deploy).
// ---------------------------------------------------------------------------
export const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'EL Connect',
  legalName: 'EL Connect Pte Ltd',
  url: BASE_URL,
  logo: 'https://elconnect-preprod.sgp1.cdn.digitaloceanspaces.com/images/logo1.png',
  description: "Singapore's #1 part-time and gig job platform connecting jobseekers with verified employers across F&B, retail, warehouse, and events industries.",
  foundingDate: '2020',
  identifier: {
    '@type': 'PropertyValue',
    name: 'UEN',
    value: '202006353G',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '745 Lorong 5 Toa Payoh #03-02',
    addressLocality: 'Singapore',
    postalCode: '319455',
    addressCountry: 'SG',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+6568029119',
    email: 'digitalm@elconnect.sg',
    contactType: 'customer support',
    areaServed: 'SG',
    availableLanguage: 'English',
  },
  sameAs: [
    'https://www.facebook.com/elconnectsgp',
    'https://www.instagram.com/elconnectsgp',
    'https://sg.linkedin.com/company/el-connect-singapore',
    'https://www.youtube.com/c/elconnect',
    'https://apps.apple.com/sg/app/el-connect-part-time-jobs/id1515715893',
    'https://play.google.com/store/apps/details?id=com.elconnectptuser',
  ],
};

// ---------------------------------------------------------------------------
// LocalBusiness — physical office (Semrush "Local Schema" + Local Contact Details: phone / opening
// hours / geo). Injected site-wide via main.js router.beforeEach (right after the per-route ld+json
// wipe) so it survives in prerendered + raw HTML on every route. Keep geo/hours/telephone in sync with
// the footer + the contact-us map iframe.
// ---------------------------------------------------------------------------
export const LOCAL_BUSINESS = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${BASE_URL}/#localbusiness`,
  name: 'EL Connect',
  legalName: 'EL Connect Pte Ltd',
  url: BASE_URL,
  logo: 'https://elconnect-preprod.sgp1.cdn.digitaloceanspaces.com/images/logo1.png',
  image: 'https://elconnect-preprod.sgp1.cdn.digitaloceanspaces.com/images/logo1.png',
  telephone: '+6568029119',
  email: 'digitalm@elconnect.sg',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '745 Lorong 5 Toa Payoh #03-02',
    addressLocality: 'Singapore',
    postalCode: '319455',
    addressCountry: 'SG',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 1.333408,
    longitude: 103.852831,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  hasMap: 'https://maps.google.com/?q=1.333408,103.852831',
  parentOrganization: { '@id': `${BASE_URL}/#organization` },
  sameAs: [
    'https://www.facebook.com/elconnectsgp',
    'https://www.instagram.com/elconnectsgp',
    'https://sg.linkedin.com/company/el-connect-singapore',
  ],
};

// ---------------------------------------------------------------------------
// S2 — WebSite + SearchAction (homepage only)
// ---------------------------------------------------------------------------
export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: 'EL Connect',
    url: BASE_URL,
    publisher: { '@id': `${BASE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/singapore-part-time-jobs/all-categories-jobs/all-companies-jobs/{search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// ---------------------------------------------------------------------------
// S3 — BreadcrumbList
// Each route maps to its breadcrumb trail: array of { name, url } from root.
// Home is always position 1. Add new routes here as pages are built.
// ---------------------------------------------------------------------------
const BREADCRUMBS = {
  '/': null, // homepage — no breadcrumb needed

  '/singapore-jobs':            [{ name: 'Jobs in Singapore', url: `${BASE_URL}/singapore-jobs` }],
  '/singapore-part-time-jobs':  [{ name: 'Part-Time Jobs Singapore', url: `${BASE_URL}/singapore-part-time-jobs` }],
  '/singapore-full-time-jobs':  [{ name: 'Full-Time Jobs Singapore', url: `${BASE_URL}/singapore-full-time-jobs` }],
  '/singapore-gig-missions':    [{ name: 'Gig Missions Singapore', url: `${BASE_URL}/singapore-gig-missions` }],
  '/gig-missions':              [{ name: 'Gig Missions', url: `${BASE_URL}/gig-missions` }],

  '/jobseeker':   [{ name: 'For Jobseekers', url: `${BASE_URL}/jobseeker` }],
  '/employer':    [{ name: 'For Employers', url: `${BASE_URL}/employer` }],
  '/about-us':    [{ name: 'About Us', url: `${BASE_URL}/about-us` }],
  '/faq':         [{ name: 'FAQ', url: `${BASE_URL}/faq` }],
  '/blog':        [{ name: 'Blog', url: `${BASE_URL}/blog` }],
  '/rewards':     [{ name: 'Rewards', url: `${BASE_URL}/rewards` }],
  '/resume-builder':     [{ name: 'AI Resume Builder', url: `${BASE_URL}/resume-builder` }],

  '/terms-of-service': [{ name: 'Terms of Service', url: `${BASE_URL}/terms-of-service` }],
  '/privacy-policy':   [{ name: 'Privacy Policy', url: `${BASE_URL}/privacy-policy` }],

  '/login-jobseeker':  [{ name: 'Jobseeker Login', url: `${BASE_URL}/login-jobseeker` }],
  '/login-employer':   [{ name: 'Employer Login', url: `${BASE_URL}/login-employer` }],
  '/signup-jobseeker': [{ name: 'Sign Up', url: `${BASE_URL}/signup-jobseeker` }],
  '/signup-employer':  [{ name: 'Sign Up as Employer', url: `${BASE_URL}/signup-employer` }],
};

/**
 * Returns a schema.org BreadcrumbList object for the given route path,
 * or null if no breadcrumb is defined for that path.
 *
 * Usage in a component's mounted():
 *   import { getBreadcrumbs } from '@/schema.config';
 *   const schema = getBreadcrumbs(this.$route.path);
 *   if (schema) injectJsonLd('breadcrumb-schema', schema);
 */
export function getBreadcrumbs(routePath) {
  // Strip trailing slash and query string for lookup
  const path = routePath.replace(/\/$/, '').split('?')[0];
  const trail = BREADCRUMBS[path];
  if (trail === null || trail === undefined) return null;

  const items = [
    { name: 'Home', url: BASE_URL },
    ...trail,
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Helper to inject/replace a <script type="application/ld+json"> tag in <head>.
 * id must be unique per schema type on the page.
 *
 * Usage:
 *   import { injectJsonLd } from '@/schema.config';
 *   injectJsonLd('breadcrumb-schema', getBreadcrumbs(this.$route.path));
 */
// ---------------------------------------------------------------------------
// S4 — FAQPage
// Page-specific Q&As. Keep answers concise (≤300 chars) — Google truncates longer ones.
// The /faq page uses DB-driven FAQs injected dynamically; this covers the static
// FAQ blocks on /jobseeker and /employer.
// ---------------------------------------------------------------------------
// Set to false to hide the FAQ block on that page without touching the component.
export const FAQ_ENABLED = {
  jobseeker: true,
  employer: true,
};

export const FAQ_DATA = {
  jobseeker: [
    {
      q: 'How do I find part-time jobs on EL Connect?',
      a: 'Download the EL Connect app, create a free account, browse available shifts by category or location, and apply with one tap. Most employers respond within 24 hours.',
    },
    {
      q: 'What types of jobs are available on EL Connect?',
      a: 'EL Connect lists part-time, full-time, and gig jobs across F&B, retail, warehouse, events, admin, customer service, and more across Singapore.',
    },
    {
      q: 'Is it free to sign up as a jobseeker?',
      a: 'Yes, creating a jobseeker account on EL Connect is completely free. There are no subscription or placement fees.',
    },
    {
      q: 'I\'m a foreigner. Can I apply?',
      a: 'Our platform is open to Singapore Citizen, Singapore PR, LTVP (with PLOC) or Student pass (MOM approved institution) holder, age above 16 years old only.',
    },
    {
      q: 'How will I get paid?',
      a: 'Payments are made via PayNow or FAST. After job completion, your earnings will credited to your in-app Wallet, where you can request a payout anytime.',
    },
    {
      q: 'What is a Gig Mission?',
      a: 'Gig Missions are short, flexible tasks posted by employers — ideal for earning extra income without committing to a regular schedule. Browse them under the Gig Missions tab.',
    },
  ],

  employer: [
    {
      q: 'How quickly can I find part-time workers through EL Connect?',
      a: 'Most job posts on EL Connect receive applications within hours. With our verified worker pool of 100,000+ jobseekers, you can fill shifts same-day for urgent requirements.',
    },
    {
      q: 'Are there agency fees for hiring through EL Connect?',
      a: 'No agency fees. EL Connect uses a transparent subscription and per-post model. You only pay for what you use — no hidden placement or commission charges.',
    },
    {
      q: 'Can I hire workers for a single day or a recurring schedule?',
      a: 'Yes. EL Connect supports one-off daily shifts, recurring part-time schedules, full-time hiring, and Gig Missions — all from a single employer dashboard.',
    },
    {
      q: 'How do I get started and create an account to post jobs?',
      a: 'Getting started is simple! Visit our website and create an employer account. Once registered, our Sales Team will get in touch to guide you through the onboarding process, explain how the platform works, and help you start posting jobs quickly.',
    },    
    {
      q: 'How are jobseekers verified on EL Connect?',
      a: 'Jobseekers complete identity verification (including MyInfo/Singpass integration), providing employers with reliable worker profiles before making hiring decisions.',
    },
    {
      q: 'What industries does EL Connect serve?',
      a: 'EL Connect serves F&B, retail, warehouse & logistics, events, admin, customer service, cleaning, and more across Singapore.',
    },
  ],
};

// TH deployment: Thai Q&As with TH rules (Thai citizens, 18+, Thai National ID, baht).
// Draft 2026-09-24 — to be polished by the native Thai reviewer (THAILAND.md §11.2).
export const FAQ_DATA_TH = {
  jobseeker: [
    {
      q: 'หางานพาร์ทไทม์บน EL Connect ได้อย่างไร?',
      a: 'ดาวน์โหลดแอป EL Connect สร้างบัญชีฟรี ค้นหากะงานตามประเภทงานหรือสถานที่ แล้วกดสมัครได้ในคลิกเดียว',
    },
    {
      q: 'มีงานประเภทใดบ้างบน EL Connect?',
      a: 'EL Connect มีงานพาร์ทไทม์และงานรายวันหลากหลายสาขา เช่น ร้านอาหารและเครื่องดื่ม ค้าปลีก คลังสินค้า อีเวนต์ งานธุรการ และบริการลูกค้า ในประเทศไทย',
    },
    {
      q: 'สมัครเป็นผู้หางานมีค่าใช้จ่ายหรือไม่?',
      a: 'ไม่มีค่าใช้จ่าย การสร้างบัญชีผู้หางานบน EL Connect ฟรีทั้งหมด ไม่มีค่าสมาชิกหรือค่านายหน้า',
    },
    {
      q: 'ใครสามารถสมัครได้บ้าง?',
      a: 'ขณะนี้เปิดรับเฉพาะผู้ที่มีสัญชาติไทยและมีอายุ 18 ปีขึ้นไป โดยต้องยืนยันตัวตนด้วยเลขประจำตัวประชาชนไทย',
    },
    {
      q: 'ฉันจะได้รับเงินอย่างไร?',
      a: 'การชำระเงินจะดำเนินการผ่านการโอนเงินผ่านธนาคาร เมื่อเสร็จสิ้นงาน รายได้ของคุณจะถูกโอนเข้ากระเป๋าเงินในแอป ซึ่งคุณสามารถกดถอนเงินได้ทุกเมื่อที่ต้องการ',
    },    
    {
      q: 'Gig Mission คืออะไร?',
      a: 'Gig Mission คืองานระยะสั้นที่ยืดหยุ่นซึ่งนายจ้างโพสต์ไว้ เหมาะสำหรับหารายได้เสริมโดยไม่ต้องผูกมัดกับตารางงานประจำ ดูได้ที่แท็บ Gig Missions',
    },
  ],

  employer: [
    {
      q: 'หาพนักงานพาร์ทไทม์ผ่าน EL Connect ได้เร็วแค่ไหน?',
      a: 'ประกาศงานส่วนใหญ่ได้รับใบสมัครภายในไม่กี่ชั่วโมง สำหรับงานเร่งด่วนคุณสามารถหาคนมาทำงานได้ภายในวันเดียวกัน',
    },
    {
      q: 'มีค่านายหน้าในการจ้างงานผ่าน EL Connect หรือไม่?',
      a: 'ไม่มีค่านายหน้า EL Connect คิดค่าบริการอย่างโปร่งใส คุณจ่ายเฉพาะที่ใช้จริง ไม่มีค่าธรรมเนียมแอบแฝง',
    },
    {
      q: 'จ้างงานแบบวันเดียวหรือแบบประจำได้หรือไม่?',
      a: 'ได้ EL Connect รองรับทั้งกะงานรายวัน ตารางงานพาร์ทไทม์แบบประจำ และ Gig Missions จัดการทั้งหมดได้จากแดชบอร์ดนายจ้างเดียว',
    },
    {
      q: 'เริ่มต้นและสร้างบัญชีเพื่อประกาศงานได้อย่างไร?',
      a: 'เริ่มต้นง่ายๆ เพียงสร้างบัญชีนายจ้างบนเว็บไซต์ของเรา เมื่อลงทะเบียนแล้ว ทีมขายจะติดต่อกลับเพื่อแนะนำการใช้งานและช่วยให้คุณเริ่มประกาศงานได้อย่างรวดเร็ว',
    },
    {
      q: 'EL Connect ตรวจสอบผู้หางานอย่างไร?',
      a: 'ผู้หางานทุกคนต้องยืนยันตัวตนด้วยเลขประจำตัวประชาชนไทย นายจ้างจึงได้ข้อมูลผู้สมัครที่เชื่อถือได้ก่อนตัดสินใจจ้างงาน',
    },
    {
      q: 'EL Connect ให้บริการอุตสาหกรรมใดบ้าง?',
      a: 'EL Connect ให้บริการธุรกิจร้านอาหารและเครื่องดื่ม ค้าปลีก คลังสินค้าและโลจิสติกส์ อีเวนต์ งานธุรการ บริการลูกค้า งานทำความสะอาด และอื่นๆ ในประเทศไทย',
    },
  ],
};

// The Q&As for a page on this deployment (TH gets the Thai set).
export function getFAQItems(pageKey, isTH) {
  return (isTH ? FAQ_DATA_TH : FAQ_DATA)[pageKey] || [];
}

/**
 * Returns a schema.org FAQPage object for the given page key ('jobseeker' | 'employer').
 * For the /faq page, pass the live DB-fetched faqs array directly to getFAQSchemaFromList().
 */
export function getFAQSchema(pageKey, isTH = false) {
  const items = getFAQItems(pageKey, isTH);
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/**
 * Builds a FAQPage schema from a live DB-fetched array (used by Faq.vue).
 * Each item should have { question, answer } fields.
 */
export function getFAQSchemaFromList(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/(<([^>]+)>)/gi, ''),
      },
    })),
  };
}

export function injectJsonLd(id, schema) {
  if (!schema) return;
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(schema, null, 2);
  document.head.appendChild(script);
}
